import axios from "axios/index";
import {setServerCall} from "../Utils/rest";
import {get, set} from 'lodash'
import React from "react";
import {AlertDialog} from "../Components/Smart/AlertDialog/AlertDialog";
import {getAccessToken} from "../Services/auth";
import {withUserContext} from "../contexts/UserContext";
import {withI18nContext} from "../contexts/I18nContext";
import pako from 'pako';

/**
 * Normalizes an axios error into a standardized format for component-level handling.
 * @param {object} error - The axios error object
 * @returns {object} Normalized error object with {error, params, errorMessage}
 */
const normalizeErrorForComponent = (error) => ({
    error: get(error, 'response.data.meta.type'),
    params: get(error, 'response.data.meta.params'),
    errorMessage: get(error, 'response.data.meta.errorMessage')
});

const API_PORT = process.env.REACT_APP_API_PORT
const locationArray = window.location.href.split(':')
const BASE_API = process.env.REACT_APP_BASE_CLIENT || `https:${locationArray[1]}:${API_PORT}/`;

let serverCall = axios.create({
    baseURL: BASE_API
});
setServerCall(serverCall)

class APIHandlerInner extends React.Component {
    constructor(props) {
        super(props)

        const that = this;

        serverCall.interceptors.request.use((config) => {
                config.headers = config.headers || {};
                const accessToken = getAccessToken();
                if (accessToken) {
                    config.headers.Authorization = accessToken;
                }
                // Only gzip if the request has a body (e.g., POST/PUT) and not form data
                if (config.data && typeof config.data === 'object' && !config.data.constructor.name.includes('FormData')) {
                    const jsonString = JSON.stringify(config.data);
                    const compressed = pako.gzip(jsonString);
                    config.data = compressed;
                    config.headers['Content-Encoding'] = 'gzip';
                    config.headers['Content-Type'] = 'application/json';
                }

                return config;
            }, (error) => Promise.reject(error)
        );
        serverCall.interceptors.response.use((res) => {
                if (this.state.apiError === 'UNAUTHORIZED' && res.request.responseURL.includes('login')) {
                    this.setState({
                        apiError: ""
                    })
                }
                return res
            }, (error) => {
                if (get(error, 'response.data.constructor') === ArrayBuffer) {
                    const decodedDataString = String.fromCharCode.apply(null, new Uint8Array(get(error, 'response.data')));
                    const data = JSON.parse(decodedDataString);
                    set(error, 'response.data', data)
                }
                if (get(error, 'response.status') === 401) {
                    if (this.state.apiError !== 'UNAUTHORIZED') {
                        that.setState({apiError: 'UNAUTHORIZED'})
                    }
                }
                if (get(error, 'response.status') === 429) {
                    if (this.state.apiError !== 'TOO_MANY_REQUESTS') {
                        that.setState({apiError: 'TOO_MANY_REQUESTS', reason: error.response.data})
                    }
                }
                if (get(error, 'response.status') === 457) {
                    that.setState({apiError: 'EMPTY_LOGIN'})
                    return Promise.reject({cancelled: true})
                }
                if (get(error, 'response.status') === 400) {
                    const handleByComponent = get(error, 'config.handleErrorByComponent', false)
                    if (handleByComponent) {
                        return Promise.reject(normalizeErrorForComponent(error))
                    }
                    that.setState({
                        apiError: 'WRONG_INPUT',
                        errorMessage: get(error, 'response.data.meta.errorMessage')
                    })
                }
                if (get(error, 'response.status') === 403) {
                    that.setState({
                        apiError: 'FORBIDDEN',
                        errorMessage: get(error, 'response.data.meta.errorMessage')
                    })
                }
                if (get(error, 'response.status') === 409) {
                    const handleByComponent = get(error, 'config.handleErrorByComponent', false)
                    if (handleByComponent) {
                        return Promise.reject(normalizeErrorForComponent(error))
                    }
                    //errors with status 409 are not errors, they are just messages
                    //they also have parameters that are needed to be shown
                    that.setState({
                        apiError: 'FORBIDDEN',
                        errorMessage: this.props.i18n.translate(`Errors_409.${get(error, 'response.data.meta.type')}`, {context: {...get(error, 'response.data.meta.params')}})
                    })
                }
                if (get(error, 'response.status') === 500) {
                    if (get(error, 'config.handleErrorByComponent', false)) {
                        return Promise.reject(normalizeErrorForComponent(error))
                    }
                    that.setState({
                        apiError: 'SERVER_ERROR',
                        errorMessage: get(error, 'response.data.meta.errorMessage', '')
                    })
                }
                return Promise.reject(error)
            }
        );
        this.state = {
            apiError: "",
            clearErrors: this.clearErrors
        }

    }

    clearErrors = () => {
        this.setState(
            {
                apiError: "",
                errorMessage: ""
            })
    }

    clearErrorsWithRedirect = (path) => {
            this.clearErrors()
            window.location.href = path;
    }

    handleLogoutRedirect = () => {
        this.clearErrorsWithRedirect('/logout');
    }

    render() {
        const {clearErrors, errorMessage, apiError, reason} = this.state
        const {i18n} = this.props;
        
        if (apiError === 'SERVER_ERROR') {
            return <AlertDialog title={i18n.translate('errors.serverError')}
                                alertMessage={errorMessage || i18n.translate('errors.generalServerError')}
                                onOk={clearErrors}/>
        }
        if (apiError === 'WRONG_INPUT') {
            return <AlertDialog title={i18n.translate('errors.clientError')}
                                alertMessage={errorMessage || i18n.translate('errors.serverErrorWrongInput')}
                                onOk={clearErrors}/>
        }
        if (apiError === 'NOT_FOUND') {
            return <AlertDialog title={i18n.translate('errors.clientError')}
                                alertMessage={errorMessage || i18n.translate('errors.notFound')} onOk={clearErrors}/>
        }
        if (apiError === 'NOT_ACCEPTABLE') {
            return <AlertDialog title={i18n.translate('errors.clientError')}
                                alertMessage={errorMessage || i18n.translate('errors.notAcceptable')}
                                onOk={clearErrors}/>
        }
        if (apiError === 'FORBIDDEN') {
            return <AlertDialog title={i18n.translate('errors.clientError')}
                                alertMessage={errorMessage || i18n.translate('errors.forbidden')}
                                onOk={clearErrors}/>
        }
        if (apiError === 'EMPTY_LOGIN') {
            return <AlertDialog title={i18n.translate(`errors.clientError`)}
                                alertMessage={errorMessage ||  i18n.translate('errors.session_expired')}
                                onOk={this.handleLogoutRedirect}/>
        }
        if (apiError === 'TOO_MANY_REQUESTS') {
            return <AlertDialog title={i18n.translate(`errors.clientError`)}
                                alertMessage={errorMessage || i18n.translate(`errors.${reason}`)}
                                onOk={clearErrors}/>
        }
        return ''
    }
}

export const APIHandler = withUserContext(withI18nContext(APIHandlerInner))